import { Button, Input } from '@ws/shared/components';
import { IListedMcpToken } from '@ws/shared/resources';
import { TCustomer } from '@ws/shared/types';
import { copyToClipboard } from '@ws/shared/utils';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Loader } from '../../components/Loader';
import { Page } from '../../components/Page';
import { getResourceManager } from '../../resources';

import styles from './AccountPage.module.css';

const AUTH_STATUS = {
  AUTHED: 'AUTHED',
  UNAUTHED: 'UNAUTHED',
  LOADING: 'LOADING',
};

const TOKEN_STATE = {
  IDLE: 'IDLE',
  NAMING: 'NAMING',
  LOADING: 'LOADING',
  SUCCESS: 'SUCCESS',
};

export function AccountPage() {
  const [authStatus, setAuthStatus] = useState<string>(AUTH_STATUS.LOADING);
  const [customer, setCustomer] = useState<TCustomer | null>(null);

  const [tokenState, setTokenState] = useState<string>(TOKEN_STATE.IDLE);
  const [tokenName, setTokenName] = useState<string>('');
  const [issuedToken, setIssuedToken] = useState<string>('');

  const [tokens, setTokens] = useState<IListedMcpToken[]>([]);
  const [tokensLoaded, setTokensLoaded] = useState<boolean>(false);

  async function checkUser() {
    const { UserService } = getResourceManager();
    const { isOk, customer: customerData } = await UserService.getInfo();

    if (isOk && customerData) {
      setAuthStatus(AUTH_STATUS.AUTHED);
      setCustomer(customerData);
    } else {
      setAuthStatus(AUTH_STATUS.UNAUTHED);
      setCustomer(null);
    }
  }

  // refreshTokens — отдельный fetch списка. Вызывается при первом mount после
  // AUTHED + после каждой successful issue/revoke. Не падаем на ошибке — list
  // показывает что есть; AuthService.notifier сам тостнёт причину.
  async function refreshTokens() {
    const { AuthService } = getResourceManager();
    const { isOk, tokens: list } = await AuthService.listMcpTokens();
    if (isOk) {
      setTokens(list);
    }
    setTokensLoaded(true);
  }

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (authStatus === AUTH_STATUS.AUTHED) {
      refreshTokens();
    }
  }, [authStatus]);

  // Hard reload to drop ?view=account and land the unauthed user on AuthPage.
  // Explicit and intentional: SPA-style state via window.location is the established pattern here.
  useEffect(() => {
    if (authStatus === AUTH_STATUS.UNAUTHED) {
      window.location.search = '';
    }
  }, [authStatus]);

  async function handleLogoutClick() {
    setAuthStatus(AUTH_STATUS.LOADING);
    const { AuthService } = getResourceManager();
    const { isOk } = await AuthService.logout();
    if (isOk) {
      toast.success('Successfully logged out!');
    } else {
      toast.error('Something went wrong!');
    }
    // Same hard reload: drop ?view=account so the user sees the auth screen.
    window.location.search = '';
  }

  function resetTokenSection() {
    setTokenState(TOKEN_STATE.IDLE);
    setTokenName('');
    setIssuedToken('');
  }

  async function handleCreateToken() {
    if (!tokenName.trim()) {
      toast.error('Введите имя токена');
      return;
    }
    setTokenState(TOKEN_STATE.LOADING);
    const { AuthService } = getResourceManager();
    const { isOk, token } = await AuthService.issueMcpToken(tokenName.trim());

    if (isOk && token) {
      setIssuedToken(token);
      setTokenState(TOKEN_STATE.SUCCESS);
      refreshTokens();
    } else {
      setTokenState(TOKEN_STATE.NAMING);
    }
  }

  async function handleCopyToken() {
    try {
      await copyToClipboard(issuedToken);
      toast.success('Токен скопирован');
    } catch {
      toast.error('Не удалось скопировать токен');
    }
  }

  async function handleRevokeToken(id: string, name: string) {
    // Простое confirm — нам не нужен фирменный modal для destructive single-user action.
    const confirmed = window.confirm(`Отозвать токен «${name}»? Это действие нельзя отменить.`);
    if (!confirmed) {
      return;
    }
    const { AuthService } = getResourceManager();
    const { isOk } = await AuthService.revokeMcpToken(id);
    if (isOk) {
      toast.success('Токен отозван');
      refreshTokens();
    }
    // notifier в AuthService сам сообщит причину при ошибке.
  }

  function formatDate(iso: string | null) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  if (authStatus === AUTH_STATUS.LOADING || authStatus === AUTH_STATUS.UNAUTHED) {
    return (
      <Page className={styles['account-page']}>
        <div className={styles['account-panel']}>
          <Loader className={styles['loader-wrap']} />
        </div>
      </Page>
    );
  }

  return (
    <Page className={styles['account-page']}>
      <div className={styles['account-panel']}>
        <div className={styles['header']}>
          <span>{`Привет, ${customer?.first_name || customer?.login}!`}</span>
          <div className={styles['header-actions']}>
            <button
              className={styles['link']}
              onClick={() => {
                // Тот же hard-reload паттерн, что для logout: SPA не маршрутизирует.
                window.location.search = '';
              }}
            >
              Назад
            </button>
            <button className={styles['link']} onClick={handleLogoutClick}>
              Выйти
            </button>
          </div>
        </div>

        {/* Список активных PAT. Сами plaintext'ы тут НЕ показываются — сервер их не хранит.
            Видим только метаданные + кнопку отозвать. После issue/revoke секция обновляется. */}
        <div className={styles['tokens-list']}>
          <div className={styles['tokens-list-title']}>MCP-токены</div>
          {tokensLoaded && tokens.length === 0 && (
            <div className={styles['tokens-empty']}>
              Пока нет активных токенов. Сгенерируй ниже.
            </div>
          )}
          {tokens.map((t) => (
            <div key={t.id} className={styles['token-row']}>
              <div className={styles['token-row-meta']}>
                <div className={styles['token-name']}>
                  {t.name}
                  {t.isReadOnly && (
                    <span className={styles['token-readonly-badge']}>read-only</span>
                  )}
                </div>
                <div className={styles['token-sub']}>
                  создан {formatDate(t.createdAt)}
                  {t.lastUsedAt && ` • использовался ${formatDate(t.lastUsedAt)}`}
                </div>
              </div>
              <button className={styles['link']} onClick={() => handleRevokeToken(t.id, t.name)}>
                Отозвать
              </button>
            </div>
          ))}
        </div>

        <div className={styles['separator']} />

        <div className={styles['section']}>
          {tokenState === TOKEN_STATE.IDLE && (
            <Button onClick={() => setTokenState(TOKEN_STATE.NAMING)}>
              Сгенерировать MCP-токен
            </Button>
          )}

          {tokenState === TOKEN_STATE.NAMING && (
            <>
              <Input
                placeholder="Имя токена"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                // Намеренный autoFocus: пользователь только что нажал «Сгенерировать»,
                // поле появилось как прямое следствие действия — фокус ожидаем.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <Button onClick={handleCreateToken} disabled={!tokenName.trim()}>
                Создать
              </Button>
              <button className={styles['link']} onClick={resetTokenSection}>
                Отмена
              </button>
            </>
          )}

          {tokenState === TOKEN_STATE.LOADING && (
            <div className={styles['loader-wrap']}>
              <Loader />
            </div>
          )}

          {tokenState === TOKEN_STATE.SUCCESS && (
            <>
              <Input value={issuedToken} readOnly />
              <Button onClick={handleCopyToken}>Скопировать</Button>
              <div className={styles['warning']}>
                Сохраните токен сейчас — повторно его увидеть не получится.
              </div>
              <button className={styles['link']} onClick={resetTokenSection}>
                Создать ещё
              </button>
            </>
          )}
        </div>
      </div>
    </Page>
  );
}
